/**
 * Strava OAuth configuration and constants.
 */

import { CollectionListHandle } from "./types";

// OAuth endpoints
export const STRAVA_OAUTH_URL = "https://www.strava.com/oauth/authorize";
export const STRAVA_TOKEN_URL = "https://www.strava.com/oauth/token";

// OAuth scopes - we only need read access to activities
export const OAUTH_SCOPES = "read,activity:read" as const;

// localStorage keys for persisting OAuth and preview data
export const STORAGE_KEYS = {
  /** CSRF state for OAuth flow */
  OAUTH_STATE: "strava_oauth_state",
  /** Preview data for users who haven't signed up yet */
  PREVIEW_DATA: "strava_preview_data",
  /** Activities list for route selection */
  ACTIVITIES_DATA: "strava_activities_data",
  /** Destination to redirect to after Strava flow completes */
  DESTINATION: "strava_destination",
} as const;

// =============================================================================
// EXPIRY TIMES
// =============================================================================
//
// Different data types have different expiry times based on their purpose:
//
// 1. OAUTH STATE (15 min) - Short-lived security token for CSRF protection.
//    Must be used within the OAuth flow duration.
//
// 2. DESTINATION (15 min) - User's intended navigation target when starting OAuth.
//    Short expiry because:
//    - Represents immediate user intent (where they clicked "Connect")
//    - If they take longer than 15 min, their original context may be stale
//    - Falling back to default collection list provides a reasonable UX
//
// 3. PREVIEW/ACTIVITIES DATA (6 hrs) - Fetched Strava activity data.
//    Longer expiry because:
//    - Matches Strava access token lifetime (security alignment)
//    - Activity data itself remains valid and useful for hours
//    - Allows users to take breaks during the flow without re-authenticating
//    - Minimizes token exposure window if localStorage is compromised via XSS
//
// =============================================================================

/** OAuth state expires after 15 minutes (in milliseconds) */
export const STATE_EXPIRY_MS = 15 * 60 * 1000;

/**
 * Preview/activities data expires after 6 hours (matching Strava access token lifetime).
 * See expiry documentation above for rationale.
 */
export const PREVIEW_DATA_EXPIRY_MS = 6 * 60 * 60 * 1000;

// Destination defaults
/**
 * Default handle for the collection list page when no specific destination is set.
 * This is the fallback navigation target after Strava activity selection.
 */
export const DEFAULT_COLLECTION_LIST_HANDLE: CollectionListHandle = "A";

/**
 * Get the Strava client ID from environment.
 * This is the public client ID, safe to expose in frontend.
 * @throws Error if REACT_APP_STRAVA_CLIENT_ID is not configured
 */
export const getStravaClientId = (): string => {
  const clientId = process.env.REACT_APP_STRAVA_CLIENT_ID;
  if (!clientId) {
    throw new Error(
      "REACT_APP_STRAVA_CLIENT_ID is not configured. " +
        "Please set this environment variable to enable Strava integration."
    );
  }
  return clientId;
};

/**
 * Get the OAuth redirect URI.
 * This must match exactly what's configured in Strava API settings.
 */
export const getStravaRedirectUri = (): string => {
  const baseUrl = process.env.REACT_APP_BASE_REDIRECT_URL ?? window.location.origin;
  return `${baseUrl}/strava/callback`;
};

/**
 * Get the Edge Function URL for token exchange.
 * Uses the Supabase project URL from environment.
 */
export const getStravaExchangeUrl = (): string => {
  const supabaseProject = process.env.REACT_APP_SUPABASE_PROJECT;
  if (!supabaseProject) {
    console.error("REACT_APP_SUPABASE_PROJECT is not configured");
    return "";
  }
  return `https://${supabaseProject}.supabase.co/functions/v1/strava-exchange`;
};
